import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PointMm } from '~/domain/geometry/patientGeometry';
import {
    clientToPatientPoint,
    pinToViewPlane,
} from '~/engine/view/planeViewGeometry';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { ViewManager } from '~/engine/view/ViewManager';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';

/**
 * The view-manager events a world-anchored overlay must redraw for, and
 * that arrive WITHOUT a Recoil write or a new `view` object: a slice
 * scroll / programmatic navigation (`planeIntersectionPointChanged`); a
 * zoom — `PlaneView.updateZoomScale` dispatches ONLY `zoomScaleChanged`,
 * so an overlay listening to the intersection event alone sits on the
 * wrong anatomy after a zoom until the next scroll; a view changing sides
 * of a fusion (`fusedImageDisplayChanged`), which decides which series'
 * annotations are drawn on it; and a registration edit
 * (`registration_changed`), which moves the annotations of a fused
 * secondary drawn alone on the pane.
 */
const PLANE_REDRAW_EVENTS: readonly string[] = [
    'planeIntersectionPointChanged',
    'zoomScaleChanged',
    'fusedImageDisplayChanged',
    'registration_changed',
];

/**
 * Re-render tick for an overlay's memoized projections: increments on
 * every occurrence of `events` (default: slice / pan / zoom / fusion side
 * / registration change, see {@link PLANE_REDRAW_EVENTS}). Include the
 * value in the projection memo's deps.
 *
 * A caller passing its own `events` must pass a render-stable array (a
 * module-level const): it is an effect dependency, so an inline literal
 * would resubscribe on every render.
 */
export function usePlaneRedrawTick(
    viewManager: ViewManager | null,
    events: readonly string[] = PLANE_REDRAW_EVENTS
): number {
    const [renderTick, setRenderTick] = useState(0);

    const forceUpdate = useCallback(() => {
        setRenderTick((prev) => prev + 1);
    }, []);

    useEffect(() => {
        if (!viewManager) {
            return;
        }
        for (const event of events) {
            viewManager.addEventListener(event, forceUpdate);
        }
        return () => {
            for (const event of events) {
                viewManager.removeEventListener(event, forceUpdate);
            }
        };
    }, [viewManager, events, forceUpdate]);

    return renderTick;
}

export interface AnnotationPointDrag<TDrag> {
    /** Begin dragging a point; call from a handle's `onMouseDown`. */
    readonly beginDrag: (event: React.MouseEvent, drag: TDrag) => void;
    /** The drag in progress, or null. */
    readonly dragging: TDrag | null;
}

/**
 * Drag interaction for editing one point of a committed annotation, shared by
 * both overlays. Each move unprojects with the same
 * `clientToPatientCoordinates` the engine tool uses, so there is no
 * coordinate-space drift, then pins onto the plane `planeCoordinateMm` names
 * to keep the annotation's points coplanar.
 *
 * The drag in progress is exposed so handles stay visible for its duration.
 */
export function useAnnotationPointDrag<TDrag>(
    view: PlaneView | null,
    planeCoordinateMm: (drag: TDrag, view: PlaneView) => number,
    onPointMoved: (drag: TDrag, pointMm: PointMm, view: PlaneView) => void
): AnnotationPointDrag<TDrag> {
    const dragRef = useRef<TDrag | null>(null);
    const [dragging, setDragging] = useState<TDrag | null>(null);

    const onMove = useCallback(
        (event: MouseEvent) => {
            const drag = dragRef.current;
            if (!drag || !view) {
                return;
            }
            const patientMm = clientToPatientPoint(
                [event.clientX, event.clientY],
                view
            );
            onPointMoved(
                drag,
                pinToViewPlane(patientMm, view, planeCoordinateMm(drag, view)),
                view
            );
        },
        [view, planeCoordinateMm, onPointMoved]
    );

    const onEnd = useCallback(() => {
        dragRef.current = null;
        setDragging(null);
    }, []);

    const startDrag = useWindowMouseDrag(onMove, onEnd);

    const beginDrag = useCallback(
        (event: React.MouseEvent, drag: TDrag) => {
            dragRef.current = drag;
            setDragging(drag);
            // useWindowMouseDrag stops propagation, so this never reaches the
            // engine, which would ignore it anyway: the target is the handle.
            startDrag(event);
        },
        [startDrag]
    );

    return { beginDrag, dragging };
}
